import { useEffect, useRef, useState } from "react";
import { canManageCourtBookings } from "../lib/access";
import { ApiError } from "../lib/api/client";
import { cancelBooking, completeBooking, createWalkInBooking, getBookingSchedule, listCourts, markBookingNoShow } from "../lib/api/endpoints";
import { blockReasonTypeLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { CourtBlockDto, CourtBookingDto, CourtDto, CreateWalkInBookingRequest } from "../types";
import { BookingCard } from "./BookingCard";
import { BookingForm } from "./BookingForm";
import { Button } from "./Button";
import { CancelBookingDialog } from "./CancelBookingDialog";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { Label } from "./Label";
import { DateTimePicker } from "./DateTimePicker";
import { Select } from "./Select";

function today(): string { return new Date().toISOString().slice(0, 10); }
function weekDates(value: string): string[] {
  const start = new Date(`${value}T12:00:00`);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date.toISOString().slice(0, 10);
  });
}
function errorMessage(error: unknown, fallback: string): string { return error instanceof ApiError ? error.detail ?? error.message : fallback; }

export function BookingsAgendaTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageCourtBookings(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [date, setDate] = useState(today);
  const [period, setPeriod] = useState<"day" | "week">("day");
  const [courtId, setCourtId] = useState("");
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [bookings, setBookings] = useState<CourtBookingDto[] | null>(null);
  const [blocks, setBlocks] = useState<CourtBlockDto[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingCancel, setPendingCancel] = useState<CourtBookingDto | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [pendingNoShow, setPendingNoShow] = useState<CourtBookingDto | null>(null);
  const [noShowing, setNoShowing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setBookings(null);
    setBlocks([]);
    setLoadError(null);
    const dates = period === "day" ? [date] : weekDates(date);
    Promise.all([listCourts(token, { page: 1, pageSize: 100, isActive: true }), ...dates.map(value => getBookingSchedule(token, { date: value, courtId: courtId || undefined }))])
      .then(([courtResult, ...schedules]) => {
        if (cancelled) return;
        setCourts(courtResult.items);
        setBookings(schedules.flatMap(schedule => schedule.bookings).sort((a, b) => a.startTime.localeCompare(b.startTime)));
        setBlocks(schedules.flatMap(schedule => schedule.blocks ?? []).sort((a, b) => a.startTime.localeCompare(b.startTime)));
      })
      .catch(error => {
        if (cancelled) return;
        setBookings([]);
        setLoadError(errorMessage(error, "Falha ao carregar a agenda de reservas."));
      });
    return () => { cancelled = true; };
  }, [token, date, period, courtId, reloadKey]);

  const updateBooking = (updated: CourtBookingDto) => setBookings(current => (current ?? []).map(item => item.id === updated.id ? updated : item));

  const submitBooking = async (payload: CreateWalkInBookingRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      const created = await createWalkInBooking(token, payload);
      setBookings(current => [...(current ?? []), created].sort((a, b) => a.startTime.localeCompare(b.startTime)));
      setShowForm(false);
      setSuccess("Reserva criada com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível criar a reserva."));
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmCancel = async (reason: string) => {
    if (!token || !pendingCancel || mutationRef.current) return;
    mutationRef.current = true;
    setCancelling(true);
    setActionError(null);
    try {
      const canceled = await cancelBooking(token, pendingCancel.id, { reason });
      updateBooking(canceled);
      setPendingCancel(null);
      setSuccess("Reserva cancelada com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível cancelar a reserva."));
    } finally {
      mutationRef.current = false;
      setCancelling(false);
    }
  };

  const complete = async (booking: CourtBookingDto) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setActionError(null);
    try {
      updateBooking(await completeBooking(token, booking.id));
      setSuccess("Reserva concluída com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível concluir a reserva."));
    } finally {
      mutationRef.current = false;
    }
  };

  const confirmNoShow = async () => {
    if (!token || !pendingNoShow || mutationRef.current) return;
    mutationRef.current = true;
    setNoShowing(true);
    setActionError(null);
    try {
      updateBooking(await markBookingNoShow(token, pendingNoShow.id));
      setPendingNoShow(null);
      setSuccess("Falta (no-show) registrada com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Não foi possível registrar a falta."));
    } finally {
      mutationRef.current = false;
      setNoShowing(false);
    }
  };

  const names = new Map(courts.map(court => [court.id, court.name]));
  const activeBookings = bookings?.filter(booking => !booking.canceledAt && !booking.completedAt) ?? [];

  return <div style={{ display: "grid", gap: 14 }}>
    {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
    {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError}<Button variant="ghost" onClick={() => setReloadKey(value => value + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button></div>}
    {actionError && <div role="alert" style={{ color: C.red, fontSize: 12 }}>{actionError}</div>}
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}><div><Label>Agenda de reservas</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Consulte os horários ocupados e registre reservas avulsas, sem cobrança ou comandas.</div></div>{canManage ? <Button onClick={() => { setActionError(null); setShowForm(true); }}>+ Nova reserva</Button> : <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end", marginTop: 14 }}><label style={{ color: C.gray, fontSize: 12 }}>Data <DateTimePicker ariaLabel="Data da agenda" mode="date" placement="below" value={date} onChange={setDate} /></label><Select ariaLabel="Período da agenda" value={period} options={[{ value: "day", label: "Somente o dia" }, { value: "week", label: "Semana inteira" }]} onChange={value => setPeriod(value as "day" | "week")} /><Select ariaLabel="Filtrar agenda por quadra" value={courtId} options={[{ value: "", label: "Todas as quadras" }, ...courts.map(court => ({ value: court.id, label: court.name }))]} onChange={setCourtId} /></div>
      {bookings === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando reservas...</div>}
      {blocks.length > 0 && <div style={{ display: "grid", gap: 9, marginTop: 14 }}>{blocks.map(block => <ScheduleBlockCard key={block.id} block={block} courtName={names.get(block.courtId ?? "") ?? "Arena inteira"} />)}</div>}
      {bookings?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhuma reserva para esta data e quadra.</div>}
      {bookings && bookings.length > 0 && <div style={{ display: "grid", gap: 9, marginTop: 14 }}>{bookings.map(booking => <BookingCard key={booking.id} booking={booking} courtName={names.get(booking.courtId) ?? "Quadra"} canManage={canManage} onCancel={() => { setActionError(null); setPendingCancel(booking); }} onComplete={() => complete(booking)} onNoShow={() => { setActionError(null); setPendingNoShow(booking); }} />)}</div>}
      {bookings && <div style={{ color: C.gray, fontSize: 11.5, marginTop: 12 }}>{activeBookings.length} horário(s) ocupado(s) no filtro atual. A disponibilidade final é validada pela API ao criar a reserva.</div>}
    </Card>
    {showForm && <BookingForm courts={courts} date={date} submitting={submitting} submitError={actionError} onSubmit={submitBooking} onCancel={() => !submitting && setShowForm(false)} />}
    {pendingCancel && <CancelBookingDialog customerName={pendingCancel.customerName} busy={cancelling} error={actionError} onConfirm={confirmCancel} onCancel={() => !cancelling && setPendingCancel(null)} />}
    {pendingNoShow && (
      <ConfirmDialog
        title="Marcar falta (no-show)"
        confirmLabel="Registrar falta"
        busy={noShowing}
        busyLabel="Registrando..."
        onConfirm={confirmNoShow}
        onCancel={() => !noShowing && setPendingNoShow(null)}
      >
        Confirmar que <b style={{ color: C.ink }}>{pendingNoShow.customerName}</b> não compareceu à reserva de <b style={{ color: C.ink }}>{timeRangeLabel(pendingNoShow)}</b>? A reserva será marcada como falta e o status da assiduidade do aluno será atualizado.
      </ConfirmDialog>
    )}
  </div>;
}

function timeRangeLabel(booking: CourtBookingDto): string {
  const t = (value: string) => new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${t(booking.startTime)}–${t(booking.endTime)}`;
}

function ScheduleBlockCard({ block, courtName }: { block: CourtBlockDto; courtName: string }) {
  return (
    <div role="status" style={{ border: `1px solid ${C.ink}`, borderRadius: 12, padding: 12, background: C.ink, color: C.white }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <b>{timeRangeLabel({ startTime: block.startTime, endTime: block.endTime } as CourtBookingDto)} · {block.title}</b>
          <div style={{ color: "C.lightSoft", fontSize: 12, marginTop: 4 }}>{courtName} · {blockReasonTypeLabel(block.reasonType)}</div>
        </div>
        <span style={{ background: C.white, color: C.ink, borderRadius: 12, padding: "4px 8px", fontSize: 11, fontWeight: 700 }}>BLOQUEADO</span>
      </div>
      {block.notes && <div style={{ color: "C.lightSoft", fontSize: 11.5, marginTop: 8 }}>{block.notes}</div>}
    </div>
  );
}
